import { HelpOutlineRounded } from '@/constants/hardfin-standard-icons';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LinkProps, useNavigate } from '@tanstack/react-router';

interface Props {
  /**
   * The type of entity that was not found (e.g., "customer", "asset", "location")
   */
  entityType: string;

  /**
   * The path to navigate to when clicking the main action button.
   * This is type-safe and must be a valid route from the route tree.
   */
  listPath: LinkProps['to'];

  /**
   * The label for the main action button (e.g., "Go to customers")
   */
  listLabel: string;

  /**
   * Whether to show the back button. Defaults to true.
   */
  showBackButton?: boolean;
}

/**
 * A reusable 404 not found component that can be used for any entity type.
 * Displays a consistent error page with navigation options.
 */
export function EntityNotFound(props: Props) {
  const navigate = useNavigate();

  const handleGoToList = () => {
    navigate({ to: props.listPath });
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      padding={4}
    >
      <HelpOutlineRounded
        sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
      />
      <Typography variant="h1" component="h1" gutterBottom>
        404
      </Typography>
      <Typography variant="h4" component="h2" gutterBottom>
        Page not found
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        maxWidth="400px"
        marginBottom={4}
      >
        The {props.entityType} you're looking for doesn't exist or has been
        moved. Let's get you back on track.
      </Typography>
      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={handleGoToList}>
          {props.listLabel}
        </Button>
        {(props.showBackButton ?? true) && (
          <Button variant="outlined" onClick={handleGoBack}>
            Go back
          </Button>
        )}
      </Stack>
    </Box>
  );
}
